/**
 * The OpenAPI document as a catalogue: one Operation per path and method.
 *
 * Nothing here knows an operationId. Holes come from parameters and body
 * properties; variants from a body `oneOf` with a `const` discriminator, a
 * required body enum, or a path enum; the class from the method and the
 * response media (an event stream, a socket, a page); roles from `security`,
 * `x-mcp`, `x-assist` and the Idempotency-Key parameter. An operation added
 * to the spec is a card the moment the spec is read again.
 *
 * Next to each Operation it keeps what the contract has no field for: the
 * MCP-shaped input schema (parameters by name, plus `body`) that Args are
 * checked against, the SSE item schema, and the x-websocket frame schemas.
 */

import type { Args, Catalogue, Hole, Json, NounKind, Operation, OpClass, Security, Variant } from '../contracts';
import { acceptsOf, escapePointer, getAt, leafOf, setAt } from './nouns';
import { deref, DRAFT_DAY, DRAFT_OCCUPIER, exampleOf, inline, isValid, mergeAllOf, refName, validate, type SchemaError } from './schema';
import { isObj, type JsonObject } from '../model/json';

const METHODS = ['get', 'post', 'delete'] as const;
/** Headers the transport or the router sets; a caller never fills them. */
const HIDDEN_HEADERS = new Set(['upgrade', 'stripe-signature', 'authorization']);

export interface TagInfo { readonly name: string; readonly summary: string; readonly description: string; readonly parent: string | null }

export interface SpecCatalogue extends Catalogue {
  /** The document itself, `$ref`s intact. */
  readonly doc: Json;
  /** Changes whenever the document's text does. */
  readonly fingerprint: string;
  readonly tags: ReadonlyMap<string, TagInfo>;
}

interface Extras {
  readonly input: JsonObject;
  readonly bodyRequired: boolean;
  readonly stream: { readonly item: Json; readonly data: Json | null } | null;
  readonly socket: { readonly receive: Json | null; readonly send: Json | null } | null;
}
const extras = new WeakMap<Operation, Extras>();

/** A short, stable hash of a text (FNV-1a, 32 bits), to tell one document from another. */
export const fingerprintOf = (text: string): string => {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
};

const str = (v: Json | undefined, fallback = ''): string => (typeof v === 'string' ? v : fallback);

// -- properties, with where each came from ------------------------------------------------------

interface RawProp { readonly name: string; readonly raw: Json; readonly container: string | null; readonly required: boolean }

/** A body's properties as the spec wrote them, through `allOf` parts, each with the component it came from. */
const rawProps = (doc: Json, schema: Json, container: string | null, depth = 0): RawProp[] => {
  if (!isObj(schema) || depth > 16) return [];
  const own = typeof schema.$ref === 'string' ? refName(schema.$ref) : container;
  const s = deref(doc, schema);
  if (!isObj(s)) return [];
  const out: RawProp[] = [];
  const required = new Set(Array.isArray(s.required) ? s.required.filter((r): r is string => typeof r === 'string') : []);
  if (Array.isArray(s.allOf)) {
    for (const part of s.allOf) {
      for (const p of rawProps(doc, part, own, depth + 1)) out.push({ ...p, required: p.required || required.has(p.name) });
    }
  }
  if (isObj(s.properties)) {
    for (const [name, raw] of Object.entries(s.properties)) out.push({ name, raw, container: own, required: required.has(name) });
  }
  // A name listed as required by one part and defined by another.
  return out.map((p) => (required.has(p.name) ? { ...p, required: true } : p));
};

const ownRef = (raw: Json): string | null => (isObj(raw) && typeof raw.$ref === 'string' ? refName(raw.$ref) : null);

const holeOf = (doc: Json, path: string, prop: RawProp, bodyRequired: boolean, prefix = '', pointerPrefix = '/body'): Hole => {
  const schema = mergeAllOf(inline(doc, prop.raw));
  const name = `${prefix}${prop.name}`;
  const ref = ownRef(prop.raw) ?? prop.container;
  const draft = { name, in: 'body' as const, ref, hidden: false, raw: prop.raw, path };
  return {
    name, in: 'body', pointer: `${pointerPrefix}/${escapePointer(prop.name)}`,
    required: bodyRequired && prop.required, schema, ref, accepts: acceptsOf(draft), hidden: false,
  };
};

/** Holes for a body's properties, and one level into arrays of objects (`legs[].hand`). */
const bodyHoles = (doc: Json, path: string, props: readonly RawProp[], bodyRequired: boolean): Hole[] => {
  const holes: Hole[] = [];
  const seen = new Set<string>();
  for (const p of props) {
    if (seen.has(p.name)) continue;
    seen.add(p.name);
    const hole = holeOf(doc, path, p, bodyRequired);
    holes.push(hole);
    const raw = deref(doc, p.raw);
    if (isObj(raw) && (raw.type === 'array') && raw.items !== undefined) {
      const items = rawProps(doc, raw.items, ownRef(raw.items) ?? p.container);
      for (const q of items) holes.push(holeOf(doc, path, q, hole.required, `${p.name}[].`, `${hole.pointer}/*`));
    }
  }
  return holes;
};

// -- variants ----------------------------------------------------------------------------------

const valuesOf = (s: Json | undefined): Json[] | null => {
  if (!isObj(s)) return null;
  if (s.const !== undefined) return [s.const];
  if (Array.isArray(s.enum)) return s.enum;
  return null;
};

const label = (v: Json): string => (typeof v === 'string' ? v : JSON.stringify(v));

interface BodyShape { readonly holes: Hole[]; readonly variants: Variant[] }

/** A body that is a `oneOf`: variants per discriminator value, or per branch when there is none. */
const oneOfShape = (doc: Json, path: string, body: Json, bodyRef: string | null, bodyRequired: boolean, params: readonly Hole[]): BodyShape | null => {
  const s = deref(doc, body);
  if (!isObj(s) || !Array.isArray(s.oneOf)) return null;
  const branches = s.oneOf.map((b) => ({ raw: b, merged: mergeAllOf(inline(doc, b)), props: rawProps(doc, b, ownRef(b) ?? bodyRef) }));
  const first = branches[0]?.merged;
  const discriminator = isObj(first) && isObj(first.properties)
    ? Object.keys(first.properties).find((k) => branches.every((b) =>
      isObj(b.merged) && Array.isArray(b.merged.required) && b.merged.required.includes(k)
      && isObj(b.merged.properties) && valuesOf(b.merged.properties[k]) !== null))
    : undefined;

  const variants: Variant[] = [];
  const union = new Map<string, { hole: Hole; count: number; requiredAll: boolean }>();
  branches.forEach((b, i) => {
    const props = b.props.filter((p) => p.name !== discriminator);
    const holes = bodyHoles(doc, path, props, bodyRequired);
    for (const h of holes) {
      const had = union.get(h.pointer);
      union.set(h.pointer, had ? { hole: had.hole, count: had.count + 1, requiredAll: had.requiredAll && h.required } : { hole: h, count: 1, requiredAll: h.required });
    }
    if (discriminator && isObj(b.merged) && isObj(b.merged.properties)) {
      for (const v of valuesOf(b.merged.properties[discriminator]) ?? []) {
        variants.push({ key: label(v), label: label(v), fixed: { [`/body/${escapePointer(discriminator)}`]: v }, holes: [...params, ...holes] });
      }
    } else {
      const title = isObj(b.merged) ? str(b.merged.title) : '';
      variants.push({ key: title || `shape-${i + 1}`, label: title || `shape ${i + 1}`, fixed: {}, holes: [...params, ...holes] });
    }
  });
  const holes: Hole[] = [...params];
  if (discriminator) {
    const values = variants.map((v) => Object.values(v.fixed)[0]!).filter((v) => v !== undefined);
    const draft = { name: discriminator, in: 'body' as const, ref: bodyRef, hidden: false, raw: { enum: values }, path };
    holes.push({
      name: discriminator, in: 'body', pointer: `/body/${escapePointer(discriminator)}`, required: bodyRequired,
      schema: { enum: values }, ref: bodyRef, accepts: acceptsOf(draft), hidden: false,
    });
  }
  for (const { hole, count, requiredAll } of union.values()) {
    holes.push({ ...hole, required: requiredAll && count === branches.length });
  }
  return { holes, variants };
};

/** Variants from a required path enum, or a required enum in the body. */
const enumVariants = (holes: readonly Hole[]): Variant[] => {
  const pick = holes.find((h) => h.in === 'path' && isObj(h.schema) && Array.isArray(h.schema.enum) && h.schema.enum.length > 1)
    ?? holes.find((h) => h.in === 'body' && h.required && !h.pointer.includes('*') && isObj(h.schema) && Array.isArray(h.schema.enum) && h.schema.enum.length > 1);
  if (!pick || !isObj(pick.schema) || !Array.isArray(pick.schema.enum)) return [];
  const rest = holes.filter((h) => h !== pick);
  return pick.schema.enum.map((v) => ({ key: label(v), label: label(v), fixed: { [pick.pointer]: v }, holes: rest }));
};

// -- responses, class, security, target -----------------------------------------------------------

/** What a response names: its `$ref`, the first `$ref` of an `allOf`, or an object component one of its properties is. */
const responseRef = (doc: Json, raw: Json | undefined): string | null => {
  if (!isObj(raw)) return null;
  if (typeof raw.$ref === 'string') return refName(raw.$ref);
  if (Array.isArray(raw.allOf)) {
    for (const part of raw.allOf) if (isObj(part) && typeof part.$ref === 'string') return refName(part.$ref);
  }
  if (isObj(raw.properties)) {
    for (const p of Object.values(raw.properties)) {
      if (isObj(p) && typeof p.$ref === 'string') {
        const target = deref(doc, p);
        if (isObj(target) && (target.type === 'object' || target.properties || target.allOf)) return refName(p.$ref);
      }
    }
  }
  return null;
};

const targetOf = (path: string, holes: readonly Hole[]): NounKind => {
  const pathParams = holes.filter((h) => h.in === 'path').map((h) => h.name);
  if (pathParams.includes('token')) return 'ask';
  if (pathParams.includes('r')) return 'relay';
  if (pathParams.includes('w')) return 'world';
  const first = path.split('/')[2] ?? '';
  switch (first) {
    case 'worlds': return 'workspace';
    case 'keys': return 'key';
    case 'grants': return 'grant';
    case 'webhooks': return 'webhook';
    case 'billing': case 'usage': return 'plan';
    case 'presets': return 'preset';
    default: return 'workspace';
  }
};

const securityOf = (doc: Json, op: JsonObject, params: readonly JsonObject[]): Security => {
  if (params.some((p) => p.in === 'header' && str(p.name).toLowerCase() === 'stripe-signature')) return 'stripe';
  const requirements = Array.isArray(op.security) ? op.security : isObj(doc) && Array.isArray(doc.security) ? doc.security : [];
  const schemes = isObj(doc) && isObj(doc.components) && isObj(doc.components.securitySchemes) ? doc.components.securitySchemes : {};
  const named = requirements.flatMap((r) => (isObj(r) ? Object.keys(r) : []));
  if (named.length === 0) return 'none';
  const format = (name: string) => { const s = schemes[name]; return isObj(s) ? str(s.bearerFormat) : ''; };
  if (named.every((n) => format(n).startsWith('sbo_') || /operator/i.test(n))) return 'operator';
  return 'key';
};

const classOf = (method: string, op: JsonObject, responses: ReadonlyMap<number, { ref: string | null; media: string }>, medias: readonly string[]): OpClass => {
  if (responses.has(101) || op['x-websocket'] !== undefined) return 'socket';
  if (medias.some((m) => m.startsWith('text/event-stream'))) return 'stream';
  if (medias.length > 0 && medias.every((m) => m.startsWith('text/html'))) return 'page';
  if (method === 'DELETE') return 'delete';
  if (method === 'GET') return 'read';
  return 'write';
};

const firstExample = (media: Json | undefined): Json | undefined => {
  if (!isObj(media)) return undefined;
  if (media.example !== undefined) return media.example;
  if (isObj(media.examples)) {
    const first = Object.values(media.examples)[0];
    if (isObj(first) && first.value !== undefined) return first.value;
  }
  return undefined;
};

// -- the catalogue ----------------------------------------------------------------------------

const tagTree = (doc: Json): Map<string, TagInfo> => {
  const tags = new Map<string, TagInfo>();
  if (isObj(doc) && Array.isArray(doc.tags)) {
    for (const t of doc.tags) {
      if (!isObj(t) || typeof t.name !== 'string') continue;
      tags.set(t.name, { name: t.name, summary: str(t.summary, t.name), description: str(t.description), parent: typeof t.parent === 'string' ? t.parent : null });
    }
  }
  return tags;
};

const sectionOf = (tags: ReadonlyMap<string, TagInfo>, tag: string | undefined): string[] => {
  const out: string[] = [];
  let at = tag;
  while (at !== undefined && !out.includes(at) && out.length < 8) {
    out.unshift(at);
    at = tags.get(at)?.parent ?? undefined;
  }
  return out;
};

function operationOf(doc: Json, tags: ReadonlyMap<string, TagInfo>, path: string, item: JsonObject, method: typeof METHODS[number], op: JsonObject): Operation | null {
  if (typeof op.operationId !== 'string') return null;
  const METHOD = method.toUpperCase() as Operation['method'];

  // Parameters: the path item's, then the operation's (which win on name and place).
  const listed = [...(Array.isArray(item.parameters) ? item.parameters : []), ...(Array.isArray(op.parameters) ? op.parameters : [])];
  const byKey = new Map<string, { p: JsonObject; ref: string | null }>();
  for (const raw of listed) {
    if (!isObj(raw)) continue;
    const p = deref(doc, raw);
    if (!isObj(p) || typeof p.name !== 'string') continue;
    byKey.set(`${str(p.in)}:${p.name}`, { p, ref: typeof raw.$ref === 'string' ? refName(raw.$ref) : null });
  }
  const params = [...byKey.values()];
  const properties: JsonObject = {};
  const required: string[] = [];
  const paramHoles: Hole[] = [];
  for (const { p, ref } of params) {
    const where = str(p.in);
    const name = str(p.name);
    if (where === 'cookie' || (where === 'header' && HIDDEN_HEADERS.has(name.toLowerCase()))) continue;
    if (where !== 'path' && where !== 'query' && where !== 'header') continue;
    const place: 'path' | 'query' | 'header' = where;
    const schema = mergeAllOf(inline(doc, p.schema ?? {}));
    const isRequired = where === 'path' || p.required === true;
    properties[name] = { ...(isObj(schema) ? schema : {}), ...(typeof p.description === 'string' ? { description: p.description } : {}) };
    if (isRequired) required.push(name);
    const hidden = where === 'header' && name.toLowerCase() === 'idempotency-key';
    const holeRef = ref ?? ownRef(p.schema ?? null);
    const draft = { name, in: place, ref: holeRef, hidden, raw: p.schema ?? null, path };
    paramHoles.push({
      name, in: place, pointer: `/${escapePointer(name)}`, required: isRequired, schema, ref: holeRef,
      accepts: acceptsOf(draft), hidden,
    });
  }

  // The body.
  const rb = op.requestBody === undefined ? undefined : deref(doc, op.requestBody);
  const media = isObj(rb) && isObj(rb.content) ? rb.content['application/json'] : undefined;
  const bodyRaw = isObj(media) ? media.schema : undefined;
  const bodyRequired = isObj(rb) && rb.required === true;
  let holes: Hole[] = [...paramHoles];
  let variants: Variant[] = [];
  if (bodyRaw !== undefined) {
    const full = inline(doc, bodyRaw);
    properties.body = { ...(isObj(full) ? full : {}), description: isObj(full) && typeof full.description === 'string' ? full.description : 'The request body.' };
    if (bodyRequired) required.push('body');
    const bodyRef = ownRef(bodyRaw);
    const shaped = oneOfShape(doc, path, bodyRaw, bodyRef, bodyRequired, paramHoles);
    if (shaped) {
      holes = shaped.holes;
      variants = shaped.variants;
    } else {
      const props = rawProps(doc, bodyRaw, bodyRef);
      if (props.length) {
        holes.push(...bodyHoles(doc, path, props, bodyRequired));
      } else {
        const schema = mergeAllOf(full);
        const draft = { name: 'body', in: 'body' as const, ref: bodyRef, hidden: false, raw: bodyRaw, path };
        holes.push({ name: 'body', in: 'body', pointer: '/body', required: bodyRequired, schema, ref: bodyRef, accepts: acceptsOf(draft), hidden: false });
      }
    }
  }
  if (variants.length === 0) variants = enumVariants(holes);

  // Responses.
  const responses = new Map<number, { ref: string | null; media: string }>();
  const medias: string[] = [];
  let stream: Extras['stream'] = null;
  for (const [code, rawResp] of Object.entries(isObj(op.responses) ? op.responses : {})) {
    const status = Number(code);
    if (!Number.isInteger(status)) continue;
    const r = deref(doc, rawResp);
    const content = isObj(r) && isObj(r.content) ? r.content : {};
    const mediaType = Object.keys(content)[0] ?? '';
    const m = content[mediaType];
    let ref = responseRef(doc, isObj(m) ? m.schema : undefined);
    if (mediaType.startsWith('text/event-stream') && isObj(m) && m.itemSchema !== undefined) {
      const item = inline(doc, m.itemSchema);
      const rawItem = deref(doc, m.itemSchema);
      const rawData = isObj(rawItem) && isObj(rawItem.properties) ? deref(doc, rawItem.properties.data ?? null) : null;
      const data = isObj(rawData) && rawData.contentSchema !== undefined ? inline(doc, rawData.contentSchema) : null;
      if (ref === null && isObj(rawData)) ref = ownRef(rawData.contentSchema ?? null);
      if (status >= 200 && status < 300) stream = { item, data };
    }
    responses.set(status, { ref, media: mediaType });
    if (status >= 200 && status < 300 && mediaType) medias.push(mediaType);
  }

  const ws = op['x-websocket'];
  const socket: Extras['socket'] = isObj(ws)
    ? { receive: ws.receive === undefined ? null : inline(doc, ws.receive), send: ws.send === undefined ? null : inline(doc, ws.send) }
    : null;

  // Examples the spec documents, as Args.
  let examples: Record<string, Json> | null = null;
  const bodyExample = firstExample(media) ?? (() => { const s = bodyRaw === undefined ? undefined : deref(doc, bodyRaw); return isObj(s) ? (s.example ?? (Array.isArray(s.examples) ? s.examples[0] : undefined)) : undefined; })();
  if (bodyExample !== undefined) examples = { body: bodyExample };
  for (const { p } of params) {
    const ex = firstExample(p);
    if (ex !== undefined && typeof p.name === 'string') examples = { ...(examples ?? {}), [p.name]: ex };
  }

  const gesture = isObj(op['x-gesture']) && typeof op['x-gesture'].held === 'string' && typeof op['x-gesture'].target === 'string'
    ? { held: op['x-gesture'].held, target: op['x-gesture'].target }
    : null;
  const opTags = Array.isArray(op.tags) ? op.tags.filter((t): t is string => typeof t === 'string') : [];
  const assist = op['x-assist'] === 'read' || op['x-assist'] === 'write' ? op['x-assist'] : null;

  const operation: Operation = {
    id: op.operationId, method: METHOD, path,
    summary: str(op.summary, op.operationId), description: str(op.description).trim(),
    tags: opTags, section: sectionOf(tags, opTags[0]),
    target: targetOf(path, holes), holes, variants,
    mcp: op['x-mcp'] !== false, assist, security: securityOf(doc, op, params.map((x) => x.p)),
    idempotent: paramHoles.some((h) => h.hidden), cls: classOf(METHOD, op, responses, medias),
    responses, examples, gesture,
  };
  extras.set(operation, {
    input: { type: 'object', properties, ...(required.length ? { required } : {}), additionalProperties: false },
    bodyRequired, stream, socket,
  });
  return operation;
}

/** The document as a catalogue. `source` says whether it came from the API or the build's snapshot. */
export const catalogueOf = (doc: Json, source: 'live' | 'snapshot', fingerprint?: string): SpecCatalogue => {
  const tags = tagTree(doc);
  const ops: Operation[] = [];
  const paths = isObj(doc) && isObj(doc.paths) ? doc.paths : {};
  for (const [path, rawItem] of Object.entries(paths)) {
    const item = deref(doc, rawItem);
    if (!isObj(item)) continue;
    for (const method of METHODS) {
      const op = item[method];
      if (!isObj(op)) continue;
      const operation = operationOf(doc, tags, path, item, method, op);
      if (operation) ops.push(operation);
    }
  }
  const byId = new Map(ops.map((o) => [o.id, o]));
  return {
    version: isObj(doc) && isObj(doc.info) ? str(doc.info.version, '0') : '0',
    source,
    ops,
    byId: (id: string) => byId.get(id),
    doc,
    fingerprint: fingerprint ?? fingerprintOf(JSON.stringify(doc)),
    tags,
  };
};

// -- what else the spec says about an operation ------------------------------------------------------

/** The operation's input as one schema: parameters by name, plus `body`. The MCP tool's inputSchema has this shape. */
export const inputSchemaOf = (op: Operation): Json | null => extras.get(op)?.input ?? null;

/** An event stream's item schema and the schema of each item's JSON `data`. */
export const streamOf = (op: Operation): { readonly item: Json; readonly data: Json | null } | null => extras.get(op)?.stream ?? null;

/** What a socket operation's frames look like (`x-websocket`): what the client receives, and may send. */
export const socketOf = (op: Operation): { readonly receive: Json | null; readonly send: Json | null } | null => extras.get(op)?.socket ?? null;

/** Whether Args fit the operation; each error names a pointer into the Args. */
export const validateArgs = (op: Operation, args: Args): SchemaError[] => {
  const input = extras.get(op)?.input;
  return input ? validate(input, args) : [];
};

/** A position field: where a hole says a run or a tick sits. */
const POSITION_LEAVES = new Set(['date', 'hour', 'parametric', 'coordinates']);

/** A moment (an object with its own position fields) with no position in it says where: the draft day. */
const placed = (schema: Json, value: Json | undefined): Json | undefined => {
  if (!isObj(value) || !isObj(schema) || !isObj(schema.properties) || schema.properties.date === undefined) return undefined;
  if (['date', 'parametric', 'coordinates'].some((k) => value[k] !== undefined)) return undefined;
  const next = { ...value, date: DRAFT_DAY };
  return isValid(schema, next) ? next : undefined;
};

/**
 * Args that can be fired as they stand: the spec's own examples first, the
 * variant's fixed values, then an example for every required hole still
 * empty. A required body with nothing required in it is sent as `{}`.
 *
 * The spec leaves a position optional (a date, or parametric + coordinates),
 * but an operation that takes one is refused without it, so a draft with
 * position holes and none filled is placed on the draft day; a moment is
 * placed the same way. An occupier is named, not numbered: id 0 is no one.
 */
export const draftOf = (op: Operation, variant?: string): Args => {
  const v = variant === undefined ? op.variants[0] : op.variants.find((x) => x.key === variant);
  const holes = v?.holes ?? op.holes;
  let args: Args = { ...(op.examples ?? {}) };
  if (v) for (const [pointer, value] of Object.entries(v.fixed)) args = setAt(args, pointer, value);
  for (const h of holes) {
    if (!h.required || h.hidden || h.pointer.includes('*') || getAt(args, h.pointer) !== undefined) continue;
    const named = h.accepts.includes('occupier') && isValid(h.schema, DRAFT_OCCUPIER);
    args = setAt(args, h.pointer, named ? DRAFT_OCCUPIER : exampleOf(h.schema));
  }
  const position = holes.filter((h) => !h.hidden && !h.pointer.includes('*') && h.accepts.includes('position') && POSITION_LEAVES.has(leafOf(h.name)));
  const day = position.find((h) => leafOf(h.name) === 'date' && isValid(h.schema, DRAFT_DAY));
  if (day && position.every((h) => getAt(args, h.pointer) === undefined)) args = setAt(args, day.pointer, DRAFT_DAY);
  for (const h of holes) {
    if (h.hidden || h.pointer.includes('*') || !h.accepts.includes('moment')) continue;
    const next = placed(h.schema, getAt(args, h.pointer));
    if (next !== undefined) args = setAt(args, h.pointer, next);
  }
  if (extras.get(op)?.bodyRequired && args.body === undefined) args = { ...args, body: {} };
  return args;
};
